import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  
  // Hidden: Order tracking for future gift system
  const [orderHistory, setOrderHistory] = useState({
    totalOrders: 0,
    totalSpent: 0,
    lastOrderDate: null,
    eligibleForGift: false,
    giftTier: null
  });

  useEffect(() => {
    // Load cart from localStorage
    const storedCart = localStorage.getItem('secret13_cart');
    if (storedCart) {
      try {
        setItems(JSON.parse(storedCart));
      } catch (e) {
        console.error('Error parsing cart:', e);
      }
    }
    
    // Hidden: Load order history for gift tracking
    const storedHistory = localStorage.getItem('secret13_order_history');
    if (storedHistory) {
      try {
        setOrderHistory(JSON.parse(storedHistory));
      } catch (e) {
        console.error('Error parsing order history:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Save cart to localStorage
    localStorage.setItem('secret13_cart', JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1) => {
    setItems(prev => {
      // Create unique key including size
      const itemKey = product.selectedSize 
        ? `${product.id}-${product.selectedSize}` 
        : product.id;
      
      const existing = prev.find(item => {
        const existingKey = item.selectedSize 
          ? `${item.id}-${item.selectedSize}` 
          : item.id;
        return existingKey === itemKey;
      });
      
      if (existing) {
        return prev.map(item => {
          const existingKey = item.selectedSize 
            ? `${item.id}-${item.selectedSize}` 
            : item.id;
          return existingKey === itemKey
            ? { ...item, quantity: item.quantity + quantity }
            : item;
        });
      }
      return [...prev, { ...product, quantity, cartKey: itemKey }];
    });
  };

  const removeItem = (cartKey) => {
    setItems(prev => prev.filter(item => {
      const itemKey = item.cartKey || item.id;
      return itemKey !== cartKey;
    }));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeItem(cartKey);
      return;
    }
    setItems(prev =>
      prev.map(item => {
        const itemKey = item.cartKey || item.id;
        return itemKey === cartKey ? { ...item, quantity } : item;
      })
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  // Hidden: Record completed order for gift system
  const recordOrder = (orderTotal) => {
    const newHistory = {
      totalOrders: orderHistory.totalOrders + 1,
      totalSpent: orderHistory.totalSpent + orderTotal,
      lastOrderDate: new Date().toISOString(),
      // Hidden gift eligibility logic (not shown to users)
      eligibleForGift: (orderHistory.totalOrders + 1) >= 3,
      giftTier: calculateGiftTier(orderHistory.totalOrders + 1, orderHistory.totalSpent + orderTotal)
    };
    setOrderHistory(newHistory);
    localStorage.setItem('secret13_order_history', JSON.stringify(newHistory));
    return newHistory;
  };

  // Hidden: Calculate gift tier based on order history
  const calculateGiftTier = (orders, spent) => {
    if (orders >= 10 || spent >= 2000) return 'platinum';
    if (orders >= 5 || spent >= 1000) return 'gold';
    if (orders >= 3 || spent >= 500) return 'silver';
    return null;
  };

  // Hidden: Check if member is eligible for gift
  const checkGiftEligibility = () => {
    return {
      eligible: orderHistory.eligibleForGift,
      tier: orderHistory.giftTier,
      ordersUntilEligible: Math.max(0, 3 - orderHistory.totalOrders)
    };
  };

  const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      total,
      itemCount,
      isOpen,
      setIsOpen,
      // Hidden: Not exposed in UI but available for future use
      recordOrder,
      checkGiftEligibility,
      orderHistory
    }}>
      {children}
    </CartContext.Provider>
  );
};
